import { useEffect, useMemo, useState } from 'react'
import { api, errorText } from '../api/client'
import type { LosExportPreview, LosExportRequest } from '../api/types'
import { useConfirm } from './Confirm'
import { Icon } from './Icon'
import { Modal } from './Modal'
import { useToast } from './Toast'
import { usd } from '../lib/format'

// The handoff to LendingPad. One file, two ways out: download it and import it by
// hand, or let the CRM import it. The dialog's job before either is to show what
// the file will say and collect the few things the CRM doesn't hold.

/** Fields the officer can fill here. Property details because a purchase has no
 *  property until there is one; SSN and date of birth because the CRM stores
 *  neither, and an export is not a reason to start — they go into this one file
 *  and are dropped. */
const PROPERTY_FIELDS: [keyof Extra, string][] = [
  ['property_street', 'Street'], ['property_city', 'City'],
  ['property_state', 'State'], ['property_zip', 'ZIP'],
]

type Extra = {
  property_street: string; property_city: string; property_state: string; property_zip: string
  loan_amount: string; purpose: string
  ssn: string; birth_date: string; co_ssn: string; co_birth_date: string
}

const EMPTY: Extra = {
  property_street: '', property_city: '', property_state: '', property_zip: '',
  loan_amount: '', purpose: '', ssn: '', birth_date: '', co_ssn: '', co_birth_date: '',
}

const PURPOSES = [
  ['purchase', 'Purchase'], ['cash_out', 'Cash-out refinance'],
  ['refinance', 'Rate/term refinance'], ['heloc', 'HELOC'], ['construction', 'Construction'],
]

export function ExportToLosModal({ subject, onClose, onPushed }: {
  subject: LosExportRequest
  onClose: () => void
  onPushed?: (loanId: string) => void
}) {
  const [extra, setExtra] = useState<Extra>(EMPTY)
  const [preview, setPreview] = useState<LosExportPreview | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<'file' | 'push' | null>(null)
  const toast = useToast()
  const confirm = useConfirm()

  // Everything the dialog could override, shown when the officer asks for it.
  const [showAll, setShowAll] = useState(false)

  /** Opening the full form fills it with what the record already holds, rather than
   *  with blanks. "Change the property" means change this property — showing empty
   *  boxes asks the officer to retype an address the file already has. */
  function openEverything() {
    setShowAll(true)
    setExtra((x) => ({
      ...x,
      property_street: x.property_street || preview?.property.street || '',
      property_city: x.property_city || preview?.property.city || '',
      property_state: x.property_state || preview?.property.state || '',
      property_zip: x.property_zip || preview?.property.zip || '',
      loan_amount: x.loan_amount || (preview?.loan.amount ? String(preview.loan.amount) : ''),
      purpose: x.purpose || preview?.loan.purpose || '',
    }))
  }

  const sent = useMemo(() => ({
    ...subject,
    extra: Object.fromEntries(Object.entries(extra).filter(([, v]) => v.trim())),
  }), [subject, extra])

  // Re-preview as the gaps are filled, so the list of what's missing shrinks while
  // you type rather than only when you submit.
  useEffect(() => {
    const t = window.setTimeout(() => {
      api.losExportPreview(sent).then((p) => { setPreview(p); setError('') })
        .catch((e) => setError(errorText(e)))
    }, 250)
    return () => window.clearTimeout(t)
  }, [sent])

  const ready = Boolean(preview && preview.problems.length === 0)
  const env = preview?.lendingpad.env ?? 'test'
  const live = env === 'live'

  async function downloadFile() {
    setBusy('file')
    try {
      await api.losExportFile(sent)
      toast('File downloaded — import it in LendingPad under New loan › Import', { tone: 'positive' })
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(null)
    }
  }

  async function push() {
    const update = Boolean(preview?.already_pushed)
    const ok = await confirm({
      title: update ? 'Overwrite the file in LendingPad?' : `Create this loan in LendingPad ${live ? 'live' : 'test'}?`,
      body: update
        ? `LendingPad has no field-level update, so loan ${preview?.already_pushed} is rewritten from this file. Anything changed in LendingPad since it was created is replaced.`
        : `A new loan for ${preview?.borrower} is created in the ${live ? 'LIVE' : 'test'} environment.`,
      confirmLabel: update ? 'Overwrite it' : 'Create the loan',
      danger: update || live,
    })
    if (!ok) return
    setBusy('push')
    try {
      const res = await api.losExportPush({ ...sent, update })
      toast(res.updated
        ? `Updated in LendingPad (${res.env})`
        : `Created in LendingPad (${res.env})${res.loan_number ? ` — loan ${res.loan_number}` : ''}`,
        { tone: 'positive' })
      onPushed?.(res.loan_id)
      onClose()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(null)
    }
  }

  const field = (key: keyof Extra, label: string, placeholder = '') => (
    <label key={key} className="fl">
      <span>{label}</span>
      <input className="field" value={extra[key]} placeholder={placeholder}
        onChange={(e) => setExtra((x) => ({ ...x, [key]: e.target.value }))} />
    </label>
  )

  const prop = preview?.property
  const typedProperty = PROPERTY_FIELDS.some(([k]) => extra[k].trim())
  // A gap is "open" while the record is missing it OR while the officer is filling
  // it in. Without the second half the box disappeared as the first digit landed:
  // the preview came back with an amount, so the field that was asking for one
  // stopped being needed and unmounted under the cursor.
  // The server owns the rule about when an address is required (urla_export.problems:
  // a purchase without one is a pre-approval and imports fine). Reading it back from
  // the preflight keeps one source of truth instead of two that drift.
  const addressBlocks = !!preview && preview.problems.some((x) => /address/i.test(x))
  const needsProperty = addressBlocks || typedProperty
  const needsAmount = !!preview && (!preview.loan.amount || !!extra.loan_amount.trim())
  const needsPurpose = !!preview && (!preview.loan.purpose || !!extra.purpose)
  // Nothing to build a file from: no property, no amount, no purpose. Six empty
  // boxes is a worse answer than saying so — an insurer or a title company is
  // never going to be the borrower on a 1003.
  const blank = !!preview && !prop?.street && !preview.loan.amount && !preview.loan.purpose
    && !typedProperty && !extra.loan_amount.trim() && !extra.purpose

  return (
    <Modal open width={580} title="Send to LendingPad"
      subtitle="A MISMO 3.4 file — download it and import it yourself, or let the CRM do it."
      onClose={onClose}
      footer={<>
        <span className="quiet" style={{ fontSize: 11.5 }}>
          {preview?.lendingpad.configured
            ? <>LendingPad <strong>{env}</strong></>
            : 'LendingPad isn’t configured on this server — download still works.'}
        </span>
        <span className="spacer" />
        <button className="btn" onClick={onClose}>Close</button>
        <button className="btn" onClick={downloadFile} disabled={!ready || busy !== null}>
          <Icon name="download" size={15} /> {busy === 'file' ? 'Preparing…' : 'Download 3.4 file'}
        </button>
        <button className="btn los" onClick={push}
          disabled={!ready || busy !== null || !preview?.lendingpad.configured}>
          <Icon name="upload" size={15} />
          {busy === 'push' ? 'Sending…' : preview?.already_pushed ? 'Update in LendingPad' : 'Send to LendingPad'}
        </button>
      </>}>

      {error ? <div className="notice danger" style={{ marginBottom: 12 }}><Icon name="error" /><span>{error}</span></div> : null}

      {preview?.already_pushed ? (
        <div className="notice warning" style={{ marginBottom: 12 }}>
          <Icon name="info" />
          <span>Already in LendingPad as loan <strong>{preview.already_pushed}</strong>. Sending again
            overwrites that file rather than creating a second one.</span>
        </div>
      ) : null}

      {preview ? (
        <>
          <dl className="export-summary">
            <div><dt>Borrower</dt><dd>{preview.borrower}{preview.co_borrower ? ' + co-borrower' : ''}</dd></div>
            <div><dt>Property</dt><dd>{[preview.property.street, preview.property.city, preview.property.state].filter(Boolean).join(', ') || '—'}</dd></div>
            <div><dt>Loan</dt><dd>{preview.loan.amount ? usd(Number(preview.loan.amount)) : '—'}{preview.loan.purpose ? ` · ${preview.loan.purpose.replace(/_/g, ' ')}` : ''}</dd></div>
            <div><dt>File</dt><dd className="mono">{preview.filename}</dd></div>
          </dl>

          {/* The gap, and nothing else. Whatever the record already holds is in the
              summary above; this asks for what is not there and gets out of the way.
              Everything else — a different property, a different purpose, an SSN —
              is behind one link, because on almost every file none of it is touched. */}
          {blank && !showAll ? (
            <div className="notice warning" style={{ marginTop: 14 }}>
              <Icon name="info" />
              <span>There is nothing on this contact to build a file from — no property, no
                loan amount and no purpose. A file comes together once there is an opportunity
                or an intake behind them.</span>
            </div>
          ) : null}

          {(!blank && (needsAmount || needsPurpose || needsProperty)) || showAll ? (
            <div className="form-grid" style={{ marginTop: 14 }}>
              {/* A purchase is the one case where the empty boxes are correct and
                  need explaining: the record holds where they live now, which is
                  emphatically not the house they are buying. */}

              {needsProperty || showAll ? PROPERTY_FIELDS.map(([key, label]) => field(key, label)) : null}
              {needsAmount || showAll ? field('loan_amount', 'Loan amount', 'e.g. 420,000') : null}
              {needsPurpose || showAll ? (
                <label className="fl">
                  <span>Purpose</span>
                  <select className="field" value={extra.purpose}
                    onChange={(e) => setExtra((x) => ({ ...x, purpose: e.target.value }))}>
                    {/* "Leave as it is" is nonsense when there is nothing to leave —
                        and when it is the gap being asked about, it is required. */}
                    <option value="">{preview.loan.purpose ? 'Leave as it is' : 'Choose one'}</option>
                    {PURPOSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </label>
              ) : null}
              {/* What the lead's own figures imply, as a sentence rather than as two
                  buttons. It is context for the number you are about to type, not a
                  decision the CRM should be offering to make. */}
              {(needsAmount || showAll) && preview.amount_suggestions.length ? (
                <p className="quiet span-2" style={{ fontSize: 11.5, margin: '-4px 0 0' }}>
                  {preview.amount_suggestions.map((sug) => `${sug.label.toLowerCase()} ${usd(sug.value)}`).join(' · ')}
                </p>
              ) : null}
              {showAll ? (
                <>
                  {field('ssn', 'SSN', '000-00-0000')}
                  {field('birth_date', 'Date of birth', 'YYYY-MM-DD')}
                  {preview.co_borrower ? field('co_ssn', 'Co-borrower SSN', '000-00-0000') : null}
                  {preview.co_borrower ? field('co_birth_date', 'Co-borrower date of birth', 'YYYY-MM-DD') : null}
                  <p className="quiet span-2" style={{ fontSize: 11.5, margin: '-4px 0 0' }}>
                    An SSN or date of birth goes into this one file and is <strong>not saved</strong>.
                  </p>
                </>
              ) : null}
            </div>
          ) : null}

          {!showAll && preview.loan.purpose === 'purchase' && !prop?.street ? (
            <p className="quiet export-needs">
              <Icon name="info" size={14} />
              <span>No property on file — normal for a pre-approval, and LendingPad takes the
                file without one. Add it under Loan goal when they’ve found the house.</span>
            </p>
          ) : null}

          {preview.problems.length && !blank ? (
            <p className="quiet export-needs">
              <Icon name="info" size={14} />
              <span>{preview.problems.join(' ')} The file can’t be built until then.</span>
            </p>
          ) : null}

          {!showAll ? (
            <button className="linklike export-more" onClick={openEverything}>
              {blank ? 'Fill it in by hand' : 'Change the property, the purpose or add an SSN'}
            </button>
          ) : null}
        </>
      ) : error ? null : <p className="quiet">Checking what the file needs…</p>}
    </Modal>
  )
}
